import React, { useCallback, useState } from 'react';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourModal from '../../components/harbour/HarbourModal';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourToast from '../../components/harbour/HarbourToast';
import FamilyConfirmSheet from './FamilyConfirmSheet';
import { apiFamilySetProfileSharing } from '../../utils/rmsApi';
import { profileSharingLabel, profileSharingChangeable } from '../../utils/familyPortal';
import './familyProfileSharing.css';

/*
 * The family's profile sharing answer on the referral view, and the way to
 * change it (TDI-482, Figma 5329:407254 "Changing the answer later"):
 *
 *   C-1  "Profile shared with the hospital team: Yes" with Change
 *   C-5  "...: No (not answered)" with Change, when the question was skipped
 *   C-2  the change sheet (phone) or modal (laptop): the same question and
 *        two choices as the consent step, Cancel and Save
 *   C-3  the toast "Your choice has been saved"
 *   C-4  read-only once the referral has an outcome, with the reason
 *
 * The server (rmsFamilySetProfileSharing) refuses a change after the outcome
 * letter, so the read-only state here is the honest mirror of that rule.
 */

export const PROFILE_SHARING_QUESTION = (childName) =>
  `Can the hospital team see ${childName}’s strengths and needs profile?`;

export function ProfileSharingChoices({ value, onChange, name = 'profile-sharing' }) {
  const option = (key, label, help) => (
    <label className={`family-profile-sharing__option${value === key ? ' family-profile-sharing__option--selected' : ''}`}>
      <input type="radio" name={name} value={key} checked={value === key} onChange={() => onChange(key)} />
      <span>
        <span className="harbour-body-16-bold">{label}</span>
        <span className="harbour-body-16">{help}</span>
      </span>
    </label>
  );
  return (
    <div className="family-profile-sharing__choices">
      {option('yes', 'Yes, they can see it', 'The team reviewing the referral can read it.')}
      {option('no', 'No, keep it between us and the school', 'The referral still goes ahead without it.')}
    </div>
  );
}

export default function FamilyProfileSharing({
  patient,
  state,
  orgId,
  patientRecordId,
  childName,
  concernLabel,
  isDesktop,
  isSecondary = false,
  onSaved,
}) {
  const label = profileSharingLabel(patient);
  const current = patient?.referralPathway?.profileSharing?.answer === 'yes' ? 'yes' : 'no';
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState(current);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [toast, setToast] = useState(false);

  const close = useCallback(() => {
    if (busy) return;
    setOpen(false);
    setFailed(false);
  }, [busy]);

  // Nothing to show until the family has been asked (older referrals were
  // consented to before the question existed), nor on a stopped referral,
  // nor for a secondary pupil, who never has a profile (TD-129/TD-137).
  if (isSecondary || !label || !state || patient?.referralPathway?.status === 'stopped') return null;

  const canChange = profileSharingChangeable(state);
  const line = `Profile shared with the hospital team: ${label}`;

  const save = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await apiFamilySetProfileSharing({ orgId, patientRecordId, answer: choice });
      setOpen(false);
      setToast(true);
      if (typeof onSaved === 'function') onSaved();
    } catch (e) {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const Dialog = isDesktop ? HarbourModal : FamilyConfirmSheet;

  return (
    <div className="family-profile-sharing">
      <div className="family-profile-sharing__row">
        <div className="family-profile-sharing__text">
          <p className="harbour-body-16">{line}</p>
          {!canChange && (
            <p className="harbour-body-16 family-profile-sharing__why">
              You can&rsquo;t change this now the referral has an outcome.
            </p>
          )}
        </div>
        {canChange && (
          <HarbourButton
            kind="tertiary"
            className="family-profile-sharing__change"
            aria-label="Change whether the hospital team can see the profile"
            onClick={() => { setChoice(current); setFailed(false); setOpen(true); }}
          >
            Change
          </HarbourButton>
        )}
      </div>

      {open && (
        <Dialog
          title={PROFILE_SHARING_QUESTION(childName)}
          confirmLabel="Save"
          confirmKind="family"
          busy={busy}
          onConfirm={save}
          onClose={close}
        >
          {concernLabel && <p className="harbour-body-16-semibold family-profile-sharing__eyebrow">{concernLabel}</p>}
          {failed && <HarbourNotice kind="error" title="We couldn’t save your choice. Try again." />}
          <ProfileSharingChoices value={choice} onChange={setChoice} name="profile-sharing-change" />
        </Dialog>
      )}

      {toast && <HarbourToast title="Your choice has been saved" onClose={() => setToast(false)} />}
    </div>
  );
}
